import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ref, onValue } from 'firebase/database';
import { database } from './firebaseConfig';
import { getStoredChatId } from './chatId';
import { MAIN_CARD_ID, CURRENCY_SYMBOLS, formatMoney } from './cardsStore';
import {
  Keypad, CardMini, evaluate, sanitize, formatResult, toNum,
} from './AmountScreen';
import {
  FX_MONTH_LIMIT, fromLabel, toLabel, cardCurrency, readRates, fxRate, displayRate, buyCurrency, cancelFx,
} from './fxStore';
import './AmountScreen.css';
import './TransferSuccessScreen.css';
import './BuyCurrencyScreen.css';

const CLOSE_MS = 320; // синхронізовано з анімаціями в BuyCurrencyScreen.css
const SHEET_CLOSE_MS = 250; // як шторка в AmountScreen.css
const TITLE = { USD: 'Купити долари', EUR: 'Купити євро' };
// «На мою Білу», «На мою доларову»
const OWN = {
  black: 'Чорну', white: 'Білу', usd: 'доларову', eur: 'єврову', kids: 'Дитячу',
  diia: 'Дія.Картку', cashback: 'Кешбек', evidnovlennia: 'єВідновлення',
};
// Мініатюри в шторці «Виберіть картку для оплати» (зі скріншотів monobank)
const THUMBS = {
  black: '/transfer/thumb-black.png',
  white: '/transfer/thumb-white.png',
  diia: '/transfer/thumb-diia.png',
  usd: '/transfer/thumb-usd.png',
  eur: '/transfer/thumb-eur.png',
  cashback: '/card-icons/cashback.png',
};
const cardName = (c) => (c.id === MAIN_CARD_ID ? 'Чорна картка' : c.name || 'Картка');

// Баланс основної картки (balance/{chatId}); решта — card.balance
function useBalanceOf() {
  const chatId = getStoredChatId();
  const [mainBalance, setMainBalance] = useState(0);
  useEffect(() => {
    if (!chatId) return undefined;
    return onValue(ref(database, `balance/${chatId}`), (snap) => setMainBalance(toNum(snap.val()?.value)));
  }, [chatId]);
  return (c) => (c.id === MAIN_CARD_ID ? mainBalance : toNum(c.balance));
}

// Кружок картки з білою мініатюрою «m» — як у «Купити долари» monobank
function CardCircle({ type }) {
  return (
    <span className={`bcs-icon bcs-icon--${type || 'black'}`}>
      <span className="bcs-icon-mini">m</span>
    </span>
  );
}

const BackArrow = ({ onClick }) => (
  <button type="button" className="bcs-back" onClick={onClick} aria-label="Назад">
    <svg viewBox="0 0 20 16">
      <path d="M19 8H1.5M8 1.2L1.2 8L8 14.8" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

// Шторка «Виберіть картку для оплати»
function SourceSheet({ sources, selected, balanceOf, onPick }) {
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  function close(card) {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(() => onPick(card), SHEET_CLOSE_MS);
  }

  return (
    <div className={`ams-sheet-root ${closing ? 'closing' : ''}`}>
      <div className="ams-sheet-backdrop" onClick={() => close(null)} />
      <div className="ams-sheet bcs-sheet">
        <div className="ams-sheet-head bcs-sheet-head">
          <p className="bcs-sheet-title">Виберіть картку для оплати</p>
        </div>
        <div className="ams-sheet-cards">
          {sources.map((c) => (
            <button type="button" className="ams-sheet-card" key={c.id} onClick={() => close(c)}>
              {THUMBS[c.type]
                ? <img className="ams-thumb" src={THUMBS[c.type]} alt="" />
                : <span className={`ams-thumb ams-thumb--${c.type}`} />}
              <span className="ams-sheet-card-text">
                <span className="ams-sheet-card-name">{cardName(c)}</span>
                <span className="ams-sheet-card-num">{formatMoney(balanceOf(c), cardCurrency(c))}</span>
              </span>
              {c.id === selected.id && (
                <svg viewBox="0 0 24 24" className="ams-check">
                  <circle cx="12" cy="12" r="12" fill="#34c759" />
                  <path d="M7 12.4l3.3 3.3L17 9" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Переказ на свою картку: сума → «Платіж надіслано». Однакова валюта — 1:1
// (як «На мою Білу»: з кнопкою регулярного платежу), різна — обмін: під
// сумою «0.02 $ за курсом …», ⇄ і, для купівлі за гривні, місячний ліміт.
// sources — картки, з яких можна платити (усі, крім target); onBack — «назад»
// з екрана суми, onDone — «Готово» або скасований платіж (закрити все).
export function OwnTransfer({
  target, initialSource, sources, fxSpent, closing: parentClosing, onBack, onDone,
}) {
  const chatId = getStoredChatId();
  const balanceOf = useBalanceOf();
  const [source, setSource] = useState(initialSource);
  const [closing, setClosing] = useState(false);
  const [expr, setExpr] = useState('');
  const [inTarget, setInTarget] = useState(false); // ⇄: вводимо в валюті картки-цілі
  const [sheet, setSheet] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const timerRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#121212';
    return () => { document.body.style.backgroundColor = prev; };
  }, []);
  // Сторінку під пальцем не тягаємо (інакше iOS прокручує весь документ)
  useEffect(() => {
    const el = rootRef.current;
    const stop = (e) => { if (e.cancelable && !e.target.closest('.tss-scroll')) e.preventDefault(); };
    el.addEventListener('touchmove', stop, { passive: false });
    return () => el.removeEventListener('touchmove', stop);
  }, []);

  const srcCur = cardCurrency(source);
  const dstCur = cardCurrency(target);
  const exchange = srcCur !== dstCur;
  const rate = fxRate(srcCur, dstCur, readRates());
  const typed = evaluate(expr);
  // Цільову суму округлюємо вниз до копійки, ціну — вгору: банк не доплачує
  const src = !exchange ? typed : inTarget ? Math.ceil(typed * rate * 100 - 1e-6) / 100 : typed;
  const dst = !exchange ? typed : inTarget ? typed : (rate ? Math.floor((typed / rate) * 100 + 1e-6) / 100 : 0);
  const buysCurrency = srcCur === 'UAH' && dstCur !== 'UAH';
  const limitLeft = FX_MONTH_LIMIT - fxSpent;
  const overBalance = src > balanceOf(source);
  const overLimit = buysCurrency && src > limitLeft;
  const canSend = rate > 0 && src > 0 && dst >= 0.01 && !overBalance && !overLimit && !sending;

  function back() {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(onBack, CLOSE_MS);
  }

  function pressKey(k) {
    if (k === 'back') setExpr((e) => e.slice(0, -1));
    else setExpr((e) => sanitize(e + k));
  }

  function pressOp(op) {
    if (op === '=') {
      if (expr) setExpr(formatResult(typed));
      return;
    }
    setExpr((e) => {
      if (!e) return e;
      if (op === '%') return /\d$/.test(e) ? `${e}%` : e;
      return /[×÷+−]$/.test(e) ? e.slice(0, -1) + op : e + op;
    });
  }

  // ⇄ — перераховуємо введене в іншу валюту, щоб сума не «стрибала»
  function swap() {
    if (expr) setExpr(formatResult(inTarget ? src : dst));
    setInTarget((v) => !v);
  }

  function pickSource(card) {
    setSheet(false);
    if (!card || card.id === source.id) return;
    setSource(card);
    setExpr('');
    setInTarget(false);
  }

  async function send() {
    if (!canSend) return;
    setSending(true);
    try {
      setSent(await buyCurrency({
        chatId, from: source, to: target, src, dst, rate,
      }));
    } catch (e) {
      setSending(false);
      window.alert(e.message === 'insufficient_funds'
        ? 'Недостатньо коштів на картці'
        : 'Не вдалося надіслати переказ. Спробуйте ще раз');
    }
  }

  async function cancel() {
    if (cancelling || !window.confirm('Скасувати платіж?')) return;
    setCancelling(true);
    try {
      await cancelFx(sent);
      onDone();
    } catch (e) {
      setCancelling(false);
      window.alert('Не вдалося скасувати платіж. Спробуйте ще раз');
    }
  }

  const bigCur = CURRENCY_SYMBOLS[inTarget ? dstCur : srcCur];
  const otherCur = CURRENCY_SYMBOLS[inTarget ? srcCur : dstCur];
  const other = inTarget ? src : dst;

  return createPortal((
    <div ref={rootRef} className={`bcs-root bcs-root--own ${parentClosing ? 'closing' : ''}`}>
      {!sent && (
        <div className={`bcs-amount ${closing ? 'closing' : ''}`}>
          <div className="bcs-amount-head">
            <BackArrow onClick={back} />
            <div className="bcs-target">
              <CardCircle type={target.type} />
              <p className="bcs-target-name">На мою {OWN[target.type] || toLabel(target.type)}</p>
            </div>
          </div>
          {buysCurrency && (
            <div className="bcs-limit">
              <img src="/transfer/fx-bank.png" alt="" />
              Залишок на придбання валюти {formatMoney(Math.max(0, limitLeft), 'UAH')}
            </div>
          )}

          <div className={`bcs-middle ${buysCurrency ? '' : 'bcs-middle--plain'}`}>
            <button type="button" className="ams-source" onClick={() => setSheet(true)}>
              <CardMini type={source.type} currency={srcCur} />
              <span>{balanceOf(source).toFixed(2)} {CURRENCY_SYMBOLS[srcCur]}</span>
              <svg viewBox="0 0 14 9" className="ams-chevron">
                <path d="M1.5 1.5L7 7l5.5-5.5" fill="none" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="bcs-amount-row">
              <p className={`ams-amount bcs-big ${expr && (overBalance || overLimit) ? 'over' : ''}`}>
                {expr ? expr.replace(/([×÷+−])/g, ' $1 ') : '0'} {bigCur}
              </p>
              {exchange && (
                <button type="button" className="bcs-swap" onClick={swap} aria-label="Змінити валюту">
                  <svg viewBox="0 0 12 12">
                    <path d="M2 4h8M7.5 1.5L10 4 7.5 6.5M10 8H2M4.5 5.5L2 8l2.5 2.5" fill="none" stroke="#e0e0e0" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
            {exchange && (
              <>
                <p className="bcs-fx">
                  {rate ? `${other.toFixed(2)} ${otherCur} за курсом ${displayRate(rate).toFixed(4)}` : 'Курс тимчасово недоступний'}
                </p>
                <p className="bcs-fee">Немає комісії</p>
              </>
            )}
          </div>

          <div className={`bcs-actions ${exchange ? '' : 'ams-actions'}`}>
            {!exchange && (
              <button type="button" className="ams-schedule" aria-label="Регулярний платіж">
                <svg viewBox="0 0 24 24">
                  <rect x="2.5" y="4.5" width="17" height="15" rx="3" fill="#8a8a8a" />
                  <path d="M6.5 2.5v4M15.5 2.5v4" stroke="#8a8a8a" strokeWidth="2" strokeLinecap="round" />
                  <rect x="2.5" y="8.5" width="17" height="1.6" fill="#272727" />
                  <circle cx="17.5" cy="17.5" r="5" fill="#272727" />
                  <path d="M20 17.5a2.5 2.5 0 1 1-.9-1.9M19.3 14.6v1.6h-1.6" fill="none" stroke="#8a8a8a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <button type="button" className="ams-send" disabled={!canSend} onClick={send}>Надіслати</button>
          </div>
          <Keypad onKey={pressKey} onOp={pressOp} />

          {sheet && (
            <SourceSheet sources={sources} selected={source} balanceOf={balanceOf} onPick={pickSource} />
          )}
        </div>
      )}

      {sent && (
        <div className="tss-root bcs-success">
          <div className="tss-scroll">
            <img
              className="tss-cat"
              src={exchange ? '/transfer/success-cat-coin.png' : '/transfer/success-cat-coins.png'}
              alt=""
            />
            <h1 className="tss-title">Платіж надіслано</h1>
            <div className="tss-row">
              <img className="tss-row-icon" src="/transfer/card-sent.png" alt="" />
              <div className="tss-row-text">
                <p className="tss-sum">
                  <b>{formatMoney(sent.src, srcCur)}</b> на вашу <b>{toLabel(target.type)}</b>
                </p>
              </div>
            </div>
          </div>
          <div className="tss-bottom">
            <button type="button" className="tss-cancel" onClick={cancel} disabled={cancelling}>Скасувати платіж</button>
            <button type="button" className="tss-done" onClick={onDone}>Готово</button>
          </div>
        </div>
      )}
    </div>
  ), document.body);
}

// «Купити долари / євро»: вибір картки-джерела → OwnTransfer.
// target — валютна картка ({ id, type, currency }), cards — усі картки
// (основна — { id: 'black' }), fxSpent — гривні на валюту цього місяця.
export default function BuyCurrencyScreen({
  target, cards, fxSpent, onClose,
}) {
  const balanceOf = useBalanceOf();
  const [closing, setClosing] = useState(false);
  const [source, setSource] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#121212';
    return () => {
      document.body.style.backgroundColor = prev;
      clearTimeout(timerRef.current);
    };
  }, []);

  const sources = cards.filter((c) => c.id !== target.id);

  function close() {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(onClose, CLOSE_MS);
  }

  const title = TITLE[cardCurrency(target)] || 'Купити валюту';

  return (
    <>
      {createPortal((
        <div className={`bcs-root ${closing ? 'closing' : ''}`}>
          <div className="bcs-pick">
            <div className="bcs-header">
              <BackArrow onClick={close} />
              <h1 className="bcs-title">{title}</h1>
            </div>
            <div className="bcs-list">
              {sources.map((c) => (
                <button type="button" className="bcs-row" key={c.id} onClick={() => setSource(c)}>
                  <CardCircle type={c.type} />
                  <span className="bcs-row-text">
                    <span className="bcs-row-name">{fromLabel(c.type)}</span>
                    <span className="bcs-row-balance">{formatMoney(balanceOf(c), cardCurrency(c))}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ), document.body)}
      {source && (
        <OwnTransfer
          target={target}
          initialSource={source}
          sources={sources}
          fxSpent={fxSpent}
          closing={closing}
          onBack={() => setSource(null)}
          onDone={close}
        />
      )}
    </>
  );
}
